
import React, { useState } from 'react';
import { Sentry } from '@/lib/sentry';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { File, Download, Eye, CalendarIcon, FileText } from 'lucide-react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { fetchInvoiceExportData } from '@/utils/invoiceExportUtils';
import { toast } from 'sonner';
import { generateInvoiceFooterText } from '@/utils/invoiceFooterUtils';
import InvoicePdfStylePreview from './InvoicePdfStylePreview';
import InvoicePreview from './InvoicePreview';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Calendar } from '@/components/ui/calendar';
import { format } from 'date-fns';
import { cn } from '@/lib/utils';
import { supabase } from '@/integrations/supabase/client';

type PreviewMode = 'regular' | 'pdf-style';

interface InvoicePreviewModalProps {
  invoiceId: string;
  triggerButton?: React.ReactNode;
  isOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
}

const InvoicePreviewModal: React.FC<InvoicePreviewModalProps> = ({
  invoiceId,
  triggerButton,
  isOpen: controlledOpen,
  onOpenChange: controlledOnOpenChange
}) => {
  const [internalOpen, setInternalOpen] = useState(false);
  const isOpen = controlledOpen !== undefined ? controlledOpen : internalOpen;
  const setIsOpen = controlledOnOpenChange || setInternalOpen;
  const [isPdfLoading, setIsPdfLoading] = useState(false);
  const [isPreviewLoading, setIsPreviewLoading] = useState(false);
  const [previewMode, setPreviewMode] = useState<PreviewMode>('pdf-style');
  const queryClient = useQueryClient();

  const { data: exportData, isLoading, error } = useQuery({
    queryKey: ['invoiceExportData', invoiceId],
    queryFn: () => fetchInvoiceExportData(invoiceId),
    enabled: isOpen,
  });

  // Get the current invoice date (fallback to created_at)
  const currentInvoiceDate = exportData?.invoice?.invoice_date 
    ? new Date(exportData.invoice.invoice_date)
    : exportData?.invoice?.created_at 
      ? new Date(exportData.invoice.created_at)
      : new Date();

  // Mutation to update invoice date
  const updateInvoiceDateMutation = useMutation({
    mutationFn: async (newDate: Date) => {
      const { error } = await supabase
        .from('invoices')
        .update({ invoice_date: newDate.toISOString() })
        .eq('id', invoiceId);
      
      if (error) throw error;
      return newDate;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['invoiceExportData', invoiceId] });
      toast.success('Invoice date updated');
    },
    onError: (error) => {
      console.error('Error updating invoice date:', error);
      toast.error('Failed to update invoice date');
    }
  });

  const handleDateChange = (date: Date | undefined) => {
    if (date) {
      updateInvoiceDateMutation.mutate(date);
    }
  };

  const generatePdfContent = async () => {
    if (!exportData) {
      toast.error('Invoice data not loaded');
      return null;
    }

    try {
      const previewElement = document.getElementById('invoice-preview');
      if (!previewElement) {
        toast.error('Preview content not found');
        return null;
      }

      const htmlContent = previewElement.innerHTML;

      const footerText = generateInvoiceFooterText({
        invoice: exportData.invoice,
        customer: exportData.customer,
        company: exportData.company,
        userProfile: exportData.userProfile,
        estimate: exportData.invoice.estimates
      }, exportData.documentDefaults);

      console.log('[InvoicePreview] Calling generate-invoice-pdf edge function');

      const { data: response, error } = await supabase.functions.invoke('generate-invoice-pdf', {
        body: {
          htmlContent,
          invoiceData: {
            invoice: exportData.invoice,
            lineItems: exportData.lineItems,
            customer: exportData.customer,
            company: exportData.company,
            userProfile: exportData.userProfile,
            documentDefaults: exportData.documentDefaults,
            footerText: footerText
          }
        }
      });

      if (error) {
        throw new Error(error.message || 'PDF generation failed');
      }

      return response;
    } catch (error) {
      console.error('[InvoicePreview] Error generating PDF content:', error);
      toast.error('Failed to generate PDF');
      return null;
    }
  };

  const handlePreviewInNewTab = async () => {
    setIsPreviewLoading(true);
    try {
      const pdfResponse = await generatePdfContent();

      if (pdfResponse?.htmlContent) {
        const screenStyles = `
          @media screen {
            body { background: #f5f5f5 !important; padding: 20px; }
            .print-container { 
              background: white; 
              max-width: 8.5in; 
              margin: 0 auto; 
              box-shadow: 0 0 10px rgba(0,0,0,0.1); 
              min-height: 11in;
              padding: 0.5in;
            }
          }
        `;

        let enhancedHtml = pdfResponse.htmlContent
          .replace('</style>', screenStyles + '</style>')
          .replace(/<body([^>]*)>/, '<body$1><div class="print-container">')
          .replace('</body>', '</div></body>');

        const printWindow = window.open('', '_blank');
        if (!printWindow) {
          toast.error('Unable to open preview. Please check your popup blocker.');
          return;
        }

        printWindow.document.write(enhancedHtml);
        printWindow.document.close();
      }
    } finally {
      setIsPreviewLoading(false);
    }
  };

  const handleDownloadPdf = async () => {
    setIsPdfLoading(true);
    try {
      const pdfResponse = await generatePdfContent();

      if (pdfResponse?.htmlContent) {
        const { downloadPdfFromHtml } = await import('@/utils/downloadPdfFromHtml');
        const filename = `invoice-${exportData?.invoice?.invoice_number || invoiceId}`;
        
        await downloadPdfFromHtml(pdfResponse.htmlContent, filename);
        toast.success('PDF downloaded successfully');
      }
    } catch (error) {
      console.error('[InvoicePreviewModal] PDF download error:', error);
      toast.error('Failed to download PDF');
    } finally {
      setIsPdfLoading(false);
    }
  };

  const defaultTrigger = (
    <Button variant="outline" size="sm">
      <File className="h-4 w-4 mr-2" />
      Preview
    </Button>
  );

  return (
    <Sentry.ErrorBoundary fallback={<div className="p-8 text-center text-destructive">Something went wrong loading the invoice preview.</div>}>
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      {triggerButton && (
        <DialogTrigger asChild>
          {triggerButton}
        </DialogTrigger>
      )}
      <DialogContent className="max-w-6xl w-full p-2 md:p-6">
        <DialogHeader>
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
            <DialogTitle className="text-base">Invoice Preview & Download</DialogTitle>
            <div className="flex items-center gap-1.5 modal-actions flex-wrap">
              {/* Invoice Date Picker */}
              {exportData && (
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      size="sm"
                      className={cn(
                        "justify-start text-left font-normal h-8",
                        !currentInvoiceDate && "text-muted-foreground"
                      )}
                      disabled={updateInvoiceDateMutation.isPending}
                    >
                      <CalendarIcon className="h-3 w-3 sm:mr-1" />
                      <span className="hidden sm:inline text-xs mr-1">Invoice Date:</span>
                      <span className="hidden sm:inline">{format(currentInvoiceDate, 'dd MMMM yyyy')}</span>
                      <span className="sm:hidden">{format(currentInvoiceDate, 'dd/MM')}</span>
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="end">
                    <Calendar
                      mode="single"
                      selected={currentInvoiceDate}
                      onSelect={handleDateChange}
                      initialFocus
                      className="p-3 pointer-events-auto"
                    />
                  </PopoverContent>
                </Popover>
              )}

              {/* Preview Mode Toggle */}
              <div className="flex items-center gap-1 bg-muted rounded-md p-1">
                <Button
                  variant={previewMode === 'regular' ? 'default' : 'ghost'}
                  size="sm"
                  onClick={() => setPreviewMode('regular')}
                  className="h-8 px-2 sm:px-3"
                >
                  <Eye className="h-3 w-3 sm:mr-1" />
                  <span className="hidden sm:inline">Regular</span>
                </Button>
                <Button
                  variant={previewMode === 'pdf-style' ? 'default' : 'ghost'}
                  size="sm"
                  onClick={() => setPreviewMode('pdf-style')}
                  className="h-8 px-2 sm:px-3"
                >
                  <FileText className="h-3 w-3 sm:mr-1" />
                  <span className="hidden sm:inline">PDF Style</span>
                </Button>
              </div>
              
              <Button
                onClick={handleDownloadPdf}
                disabled={isPdfLoading || !exportData}
                size="sm"
                className="h-8"
              >
                <Download className="h-4 w-4 sm:mr-2" />
                <span className="hidden sm:inline">{isPdfLoading ? 'Generating...' : 'Save as PDF'}</span>
              </Button>
            </div>
          </div>
        </DialogHeader>
        
        <div className="overflow-auto max-h-[70vh] border rounded-lg mt-4">
          {isLoading && (
            <div className="flex items-center justify-center py-8">
              <p>Loading invoice preview...</p>
            </div>
          )}
          
          {error && (
            <div className="flex items-center justify-center py-8">
              <p className="text-destructive">Error loading invoice preview</p>
            </div>
          )}
          
          {exportData && (
            <>
              {/* Always render PDF-style for capture; hide when in regular mode */}
              <div style={previewMode !== 'pdf-style' ? { position: 'absolute', left: '-9999px', opacity: 0, pointerEvents: 'none' } : {}}>
                <InvoicePdfStylePreview
                  invoice={exportData.invoice}
                  lineItems={exportData.lineItems}
                  customer={exportData.customer}
                  company={exportData.company}
                  userProfile={exportData.userProfile}
                  documentDefaults={exportData.documentDefaults}
                />
              </div>
              {previewMode === 'regular' && (
                <InvoicePreview
                  invoice={exportData.invoice}
                  lineItems={exportData.lineItems}
                  company={exportData.company}
                />
              )}
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
    </Sentry.ErrorBoundary>
  );
};

export default InvoicePreviewModal;
